import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

/**
 * Création rapide d'un compte Zifek SANS quitter la salle de visioconférence.
 * Après l'inscription, l'utilisateur est connecté et la salle se met à jour
 * automatiquement (aucun rechargement de page).
 */
export default function SignupModal({ open, onClose, onCreated }: Props) {
  const { registerClient } = useAuth();
  const [name, setName] = useState('');
  const [userName, setUserName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async () => {
    setError(null);
    if (!name.trim() || !userName.trim() || !email.trim() || password.length < 6) {
      setError('Remplissez tous les champs (mot de passe : 6 caractères minimum).');
      return;
    }
    setBusy(true);
    const res = await registerClient({
      name: name.trim(),
      user_name: userName.trim().toLowerCase(),
      email: email.trim(),
      password,
      telephone: '',
    });
    setBusy(false);
    if (res.success) {
      onCreated();
    } else {
      setError(res.error || "Échec de la création du compte.");
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-md bg-background-50 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-lg font-semibold font-heading text-foreground-950">Créer un compte Zifek</h3>
            <p className="text-xs text-foreground-500 mt-0.5">Votre bloc-notes sera enregistré dans vos documents.</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Nom complet</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Marie Dupont"
              className="w-full px-4 py-2.5 text-sm border border-background-200/70 rounded-xl bg-background-50 text-foreground-800 focus:outline-none focus:border-accent-300"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Nom d&apos;utilisateur</label>
            <input
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder="ex: marie"
              className="w-full px-4 py-2.5 text-sm border border-background-200/70 rounded-xl bg-background-50 text-foreground-800 focus:outline-none focus:border-accent-300"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Adresse email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@exemple.com"
              className="w-full px-4 py-2.5 text-sm border border-background-200/70 rounded-xl bg-background-50 text-foreground-800 focus:outline-none focus:border-accent-300"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Mot de passe</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="6 caractères minimum"
              className="w-full px-4 py-2.5 text-sm border border-background-200/70 rounded-xl bg-background-50 text-foreground-800 focus:outline-none focus:border-accent-300"
            />
          </div>
        </div>

        {error && <p className="text-xs text-red-500 mt-3">{error}</p>}

        <button
          onClick={submit}
          disabled={busy}
          className="mt-5 w-full py-3 bg-accent-500 text-background-50 rounded-full text-sm font-semibold cursor-pointer hover:bg-accent-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
        >
          {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-user-add-line"></i>}
          Créer mon compte
        </button>
        <p className="text-[10px] text-foreground-400 mt-3 text-center">
          Vous restez dans la réunion — aucun rechargement nécessaire.
        </p>
      </div>
    </div>
  );
}